import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  IconButton,
  Skeleton,
  Tooltip,
  TextField,
  InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SearchIcon from "@mui/icons-material/Search";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import TagOutlinedIcon from "@mui/icons-material/TagOutlined";
import TextSnippetOutlinedIcon from "@mui/icons-material/TextSnippetOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import RefreshIcon from "@mui/icons-material/Refresh";
import StarRateIcon from "@mui/icons-material/StarRate";
import HistoryIcon from "@mui/icons-material/History";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import VideocamIcon from "@mui/icons-material/Videocam";
import { useNavigate } from "react-router-dom";
import MainHeading from "../components/main-heading.component";
import { _get } from "../services/api.service";
import type { LibraryTopic } from "./library/library.data";
import MatchPickerDialog from "./library/match-picker-dialog";
import "./content-engine-v2/content-engine.css";
import "./video-create/CreateVideo.css";
import "./video-projects/VideoProjects.css";
import "./library/Library.css";

function relativeTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHrs / 24);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHrs < 24) return `${diffHrs}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const SEARCH_PAGE_SIZE = 15;

type SectionLabel = "Today" | "Yesterday" | "This Week" | "Older";

function LibrarySkeleton() {
  return (
    <div className="lib-skeleton-grid">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="lib-skeleton-card">
          <Skeleton variant="text" width="65%" sx={{ fontSize: 15, mb: 1.5 }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.4 }}>
              <Skeleton variant="circular" width={12} height={12} />
              <Skeleton variant="text" width={50} sx={{ fontSize: 11 }} />
            </Box>
            <Skeleton variant="text" width={8} sx={{ fontSize: 10 }} />
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.4 }}>
              <Skeleton variant="text" width={60} sx={{ fontSize: 11 }} />
            </Box>
          </Box>
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.3, mt: 0.5 }}>
            <Skeleton variant="circular" width={11} height={11} />
            <Skeleton variant="text" width={80} sx={{ fontSize: 11 }} />
          </Box>
        </div>
      ))}
    </div>
  );
}

function SearchGridSkeleton() {
  return (
    <div className="lib-skeleton-grid">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="lib-skeleton-card">
          <Skeleton variant="text" width="65%" sx={{ fontSize: 15, mb: 1.5 }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.4 }}>
              <Skeleton variant="circular" width={12} height={12} />
              <Skeleton variant="text" width={50} sx={{ fontSize: 11 }} />
            </Box>
            <Skeleton variant="text" width={8} sx={{ fontSize: 10 }} />
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.4 }}>
              <Skeleton variant="text" width={60} sx={{ fontSize: 11 }} />
            </Box>
          </Box>
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.3, mt: 0.5 }}>
            <Skeleton variant="circular" width={11} height={11} />
            <Skeleton variant="text" width={80} sx={{ fontSize: 11 }} />
          </Box>
        </div>
      ))}
    </div>
  );
}

const sectionConfig: Record<SectionLabel, { icon: React.ReactNode }> = {
  Today: { icon: <StarRateIcon sx={{ fontSize: 18 }} /> },
  Yesterday: { icon: <HistoryIcon sx={{ fontSize: 18 }} /> },
  "This Week": { icon: <RefreshIcon sx={{ fontSize: 18 }} /> },
  Older: { icon: <ArchiveOutlinedIcon sx={{ fontSize: 18 }} /> },
};

const Library = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState<{ label: SectionLabel; count: number; items: LibraryTopic[] }[]>([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResultTopic[]>([]);
  const [searchCount, setSearchCount] = useState(0);
  const [searchPage, setSearchPage] = useState(1);
  const [searchTotalPages, setSearchTotalPages] = useState(1);
  const [matchTopic, setMatchTopic] = useState<SearchResultTopic | null>(null);
  const fetched = useRef(false);
  const searchAbortRef = useRef<AbortController | null>(null);
  const lastSearchRef = useRef<string | null>(null);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    setLoading(true);

    _get("/content-engine/library/overview/", {}, true).then((res: any) => {
      setSections(
        (res.sections ?? []).map((s: any) => ({
          label: s.label,
          count: s.count,
          items: s.topics ?? [],
        }))
      );
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    const trimmed = search.trim();
    if (trimmed.length >= 3) {
      const timer = setTimeout(() => {
        setSearchPage(1);
        setDebouncedSearch(trimmed);
      }, 300);
      return () => clearTimeout(timer);
    }
    lastSearchRef.current = null;
    searchAbortRef.current?.abort();
    setDebouncedSearch("");
    setSearching(false);
  }, [search]);

  useEffect(() => {
    if (debouncedSearch.length < 3) return;
    const requestKey = `${debouncedSearch}|${searchPage}`;
    if (lastSearchRef.current === requestKey) return;
    lastSearchRef.current = requestKey;

    searchAbortRef.current?.abort();
    const controller = new AbortController();
    searchAbortRef.current = controller;

    setSearching(true);
    setSearchLoading(true);

    _get("/content-engine/library/search/", { params: { search: debouncedSearch, page: searchPage, page_size: SEARCH_PAGE_SIZE }, signal: controller.signal }, true)
      .then((res: any) => {
        if (controller.signal.aborted) return;
        setSearchResults(res?.results ?? []);
        setSearchCount(res?.count ?? 0);
        setSearchTotalPages(Math.max(1, Math.ceil((res?.count ?? 0) / SEARCH_PAGE_SIZE)));
      })
      .catch(() => {})
      .finally(() => {
        if (!controller.signal.aborted) setSearchLoading(false);
      });
  }, [debouncedSearch, searchPage]);

  const libraryStats = useMemo(() => {
    const totalTopics = sections.reduce((sum, s) => sum + s.count, 0);
    const todayCount = sections.find((s) => s.label === "Today")?.count ?? 0;
    const recentCount = sections
      .filter((s) => ["Today", "Yesterday", "This Week"].includes(s.label))
      .reduce((sum, s) => sum + s.count, 0);
    return { totalTopics, todayCount, recentCount };
  }, [sections]);

  return (
    <div className="container-fluid library-page video-projects-page">
      <MainHeading
        title="Library"
        subTitle="Browse topics, scripts, and versions from your content workspace"
      />
      <div className="lib-layout vp-layout">
              {loading ? (
                <LibrarySkeleton />
              ) : sections.length === 0 ? (
                <div className="vp-empty">
                  <div className="vp-empty-icon">
                    <FolderOpenOutlinedIcon sx={{ fontSize: 30 }} />
                  </div>
                  <h3 className="vp-empty-title">
                    Your library is empty
                  </h3>
                  <p className="vp-empty-text">
                    Topics you create will appear here. Start by generating your first content.
                  </p>
                  <Tooltip title="Create new content">
                    <button
                      type="button"
                      onClick={() => navigate("/content-engine")}
                      className="cv-btn cv-btn-primary"
                    >
                      <AddIcon sx={{ fontSize: 18 }} />
                      Create Your First Topic
                    </button>
                  </Tooltip>
                </div>
              ) : (
                <>
                  <div className="lib-toolbar vp-toolbar">
                    <div className="vp-stats">
                      <div className="vp-stat">
                        <span className="vp-stat-value">{libraryStats.totalTopics}</span>
                        <span className="vp-stat-label">Topics</span>
                      </div>
                      <div className="vp-stat">
                        <span className="vp-stat-value">{libraryStats.todayCount}</span>
                        <span className="vp-stat-label">Today</span>
                      </div>
                      <div className="vp-stat">
                        <span className="vp-stat-value">{libraryStats.recentCount}</span>
                        <span className="vp-stat-label">This week</span>
                      </div>
                    </div>

                    <div className="lib-toolbar-search">
                      <TextField
                        className="lib-search"
                        placeholder="Search topics or scripts..."
                        autoComplete="off"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") setSearch("");
                        }}
                        size="small"
                        fullWidth
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: "#66738a" }} /></InputAdornment>,
                          endAdornment: search ? (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => setSearch("")} sx={{ p: 0.4, color: "#66738a", "&:hover": { color: "#16213a" } }}>
                                <CloseIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </InputAdornment>
                          ) : undefined,
                        }}
                      />
                      {searching && (
                        <span className="lib-search-status">
                          {searchLoading ? "Searching..." : `${searchCount} found`}
                        </span>
                      )}
                    </div>

                    <div className="vp-toolbar-actions">
                      <Tooltip title="Create new content">
                        <button
                          type="button"
                          onClick={() => navigate("/content-engine")}
                          className="cv-btn cv-btn-primary"
                        >
                          <AddIcon sx={{ fontSize: 18 }} />
                          New Script
                        </button>
                      </Tooltip>
                    </div>
                  </div>

                  {searching ? (
                    searchLoading ? (
                      <SearchGridSkeleton />
                    ) : searchResults.length === 0 ? (
                      <div className="vp-empty lib-fade-in">
                        <div className="vp-empty-icon">
                          <SearchOffOutlinedIcon sx={{ fontSize: 30 }} />
                        </div>
                        <h3 className="vp-empty-title">
                          No topics match your search
                        </h3>
                        <p className="vp-empty-text">
                          Try different keywords or clear the search to see all your topics.
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="lib-grid">
                          {searchResults.map((topic, index) => (
                            <TopicCard key={topic.topic_id} topic={topic} index={index} query={debouncedSearch} onShowMatches={setMatchTopic} />
                          ))}
                        </div>

                        {searchTotalPages > 1 && (
                          <div className="lib-pagination">
                            <button
                              type="button"
                              className="lib-page-btn"
                              onClick={() => setSearchPage(Math.max(1, searchPage - 1))}
                              disabled={searchPage <= 1}
                            >
                              <NavigateBeforeIcon sx={{ fontSize: 18 }} />
                              Previous
                            </button>

                            <span className="lib-page-indicator">
                              Page {searchPage} of {searchTotalPages}
                            </span>

                            <button
                              type="button"
                              className="lib-page-btn"
                              onClick={() => setSearchPage(Math.min(searchTotalPages, searchPage + 1))}
                              disabled={searchPage >= searchTotalPages}
                            >
                              Next
                              <NavigateNextIcon sx={{ fontSize: 18 }} />
                            </button>
                          </div>
                        )}
                      </>
                    )
                  ) : (
                  <div className="vp-sections">
                  {sections.map((section, sectionIdx) => {
                    const config = sectionConfig[section.label];
                    return (
                      <section
                        key={section.label}
                        className="vp-section lib-fade-in"
                        style={{ animationDelay: `${sectionIdx * 0.1}s` }}
                      >
                        <div className="lib-section-head">
                          <div className="vp-section-header">
                            <span className="vp-section-icon">{config.icon}</span>
                            <h2 className="vp-section-title">
                              {section.label} ({section.count})
                            </h2>
                          </div>
                          {section.count > 6 && (
                            <Tooltip title="View all topics in this section">
                              <button
                                type="button"
                                className="lib-view-all"
                                onClick={() => navigate(`/library/${section.label.toLowerCase().replace(/\s+/g, '-')}`)}
                              >
                                View all →
                              </button>
                            </Tooltip>
                          )}
                        </div>

                        <div className="lib-grid">
                          {section.items.slice(0, 6).map((topic, index) => (
                            <TopicCard key={topic.topic_id} topic={topic} index={index} />
                          ))}
                        </div>
                      </section>
                    );
                  })}
                  </div>
                  )}
                </>
              )}
      </div>

      <MatchPickerDialog
        open={!!matchTopic}
        topic={matchTopic}
        query={debouncedSearch}
        onClose={() => setMatchTopic(null)}
      />
    </div>
  );
};

type SearchResultTopic = LibraryTopic & {
  matched_blocks?: {
    block_id: number;
    content_id: number;
    block_type?: string;
    block_type_label?: string;
    title: string;
    caption: string;
    content_format?: string | null;
  }[];
};

function MatchSnippet({ text, query }: { text: string; query: string }) {
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${escaped})`, "ig"));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <span key={i} className="lib-match-highlight">{part}</span>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

function MatchContext({ topic, query }: { topic: SearchResultTopic; query: string }) {
  const blocks = topic.matched_blocks ?? [];
  if (blocks.length === 0) {
    return (
      <div className="lib-match-title-pill">
        <TagOutlinedIcon sx={{ fontSize: 12 }} />
        <span>Matched in title</span>
      </div>
    );
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 1.2, mb: 0.5 }}>
      {blocks.slice(0, 2).map((block) => {
        const matchedInTitle = block.title && query && block.title.toLowerCase().includes(query.toLowerCase());
        const snippet = matchedInTitle ? block.title : block.caption;
        return (
          <div key={block.block_id} className="lib-match-block">
            <TextSnippetOutlinedIcon sx={{ fontSize: 12, mt: "2px", color: "#2364e8", flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <div className="lib-match-block-label">
                {block.block_type_label || block.title || "Script"}
              </div>
              {snippet && (
                <div className="lib-match-block-snippet">
                  <MatchSnippet text={snippet} query={query} />
                </div>
              )}
            </Box>
          </div>
        );
      })}
      {blocks.length > 2 && (
        <span className="lib-match-more">
          +{blocks.length - 2} more matches in scripts
        </span>
      )}
    </Box>
  );
}

function TopicCard({ topic, index, query, onShowMatches }: { topic: LibraryTopic; index: number; query?: string; onShowMatches?: (topic: SearchResultTopic) => void }) {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);

  const handleClick = () => {
    const matched = (topic as SearchResultTopic).matched_blocks ?? [];
    if (query && matched.length > 0 && onShowMatches) {
      onShowMatches(topic as SearchResultTopic);
      return;
    }
    navigate(`/library/topic/${topic.topic_id}`);
  };

  return (
    <div
      className={`lib-topic-card${hovered ? " is-hovered" : ""}`}
      style={{ animationDelay: `${Math.min(index % SEARCH_PAGE_SIZE, 5) * 0.06}s` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      role="button"
      tabIndex={0}
    >
      {topic.video_created && (
        <Tooltip title="Ready to create video">
          <span className="lib-topic-video-badge">
            <VideocamIcon sx={{ fontSize: 16 }} />
          </span>
        </Tooltip>
      )}
      <div className="lib-topic-title">
        {query ? <MatchSnippet text={capitalize(topic.title)} query={query} /> : capitalize(topic.title)}
      </div>

      {query && <MatchContext topic={topic} query={query} />}

      <div className="lib-topic-meta">
        <span className="lib-topic-meta-row">
          <ArticleOutlinedIcon sx={{ fontSize: 12 }} />
          {topic.content_count} {topic.content_count === 1 ? "Script" : "Scripts"}
        </span>

        <span>&middot;</span>

        <span className="lib-topic-meta-row">
          {topic.total_versions} {topic.total_versions === 1 ? "Version" : "Versions"}
        </span>

        <span className="lib-topic-updated">
          <ScheduleIcon sx={{ fontSize: 11 }} />
          Updated {relativeTime(topic.last_updated)}
        </span>
      </div>
    </div>
  );
}

export default Library;
